// uiarc: checkbox (https://uiarc.dev/r/checkbox.json) — Radix yerine Base UI, CSS Modules yerine Tailwind.
// 18px kare (5px yarıçap, strong kenarlık); işaretlenince `control-on` dolgusu yayla büyür, tik çizilir; belirsiz
// durumda tik üç noktayı paylaşan tireye dönüşür (şekil geçişi). Basınca yalnız kare %95'e küçülür.
// Sapma: uiarc 44px'lik düğmenin içine 18px kare koyar. Burada düzen kutusu 18px'tir (menü satırı, tablo hücresi gibi
// yoğun yüzeyleri bozmamak için); 44px dokunma alanı `after:` ile korunur, odak çerçevesi kareyi sarar.
import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'
import { motion, useReducedMotion } from 'motion/react'
import type { Transition } from 'motion/react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

/** İki işaret üç noktayı paylaşır: tik tireye ve geri dönüşür, yer değiştirmez. */
const checkPath = 'M4.25 9.25 L7.25 12.25 L13.75 5.75'
const dashPath = 'M4.75 9 L9 9 L13.25 9'

function CheckboxMark({ checked, indeterminate }: { checked: boolean; indeterminate: boolean }) {
  const reduced = useReducedMotion()
  const on = checked || indeterminate
  const fade: Transition = {
    duration: on ? motionTokens.duration.instant : motionTokens.duration.fast,
    ease: motionTokens.ease.standard,
  }
  return (
    <>
      <motion.span
        data-slot="checkbox-fill"
        className="bg-control-on absolute -inset-px rounded-[5px]"
        initial={false}
        animate={{ opacity: on ? 1 : 0, scale: on ? 1 : 0.6 }}
        transition={
          reduced ? { duration: 0 } : { scale: motionTokens.spring.snappy, opacity: fade }
        }
      />
      <svg
        className="text-control-glyph absolute -inset-px size-[18px] overflow-visible"
        viewBox="0 0 18 18"
        fill="none"
        focusable="false"
      >
        <motion.path
          initial={false}
          animate={{
            d: indeterminate ? dashPath : checkPath,
            pathLength: on ? 1 : 0,
            opacity: on ? 1 : 0,
          }}
          transition={
            reduced
              ? { duration: 0 }
              : {
                  d: motionTokens.spring.morph,
                  pathLength: motionTokens.spring.snappy,
                  opacity: fade,
                }
          }
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </>
  )
}

function Checkbox({ className, ...props }: CheckboxPrimitive.Root.Props) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        'peer border-border-strong bg-surface ease-standard relative flex size-[18px] shrink-0 cursor-pointer items-center justify-center rounded-[5px] border p-0 transition-[border-color,transform] duration-(--duration-fast) outline-none [-webkit-tap-highlight-color:transparent] after:absolute after:-inset-[13px] after:content-[""]',
        'data-checked:border-control-on data-indeterminate:border-control-on not-disabled:not-data-checked:not-data-indeterminate:hover:border-text-muted',
        'aria-invalid:border-danger not-disabled:active:scale-95 not-disabled:active:duration-(--duration-instant)',
        'data-disabled:cursor-not-allowed data-disabled:opacity-50 motion-reduce:transition-none motion-reduce:active:scale-100',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        keepMounted
        data-slot="checkbox-indicator"
        className="absolute inset-0 block"
        render={(indicatorProps, state) => (
          <span {...indicatorProps} aria-hidden="true">
            <CheckboxMark checked={state.checked} indeterminate={state.indeterminate} />
          </span>
        )}
      />
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox }
